import { useState } from "react";
import { Stack, TextField, Alert, Box } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { updatePassword } from "aws-amplify/auth";
import LoaderButton from "../components/LoaderButton";
import FormPage from "../components/FormPage";

const schema = Yup.object({
  oldPassword: Yup.string().required("Old password is required"),
  newPassword: Yup.string()
    .required("New password is required")
    .min(8, "Password too short — minimum 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
      "Must contain lowercase, uppercase, number, and special character."
    ),
  confirmPassword: Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref("newPassword")], "Passwords don't match")
});

export default function ChangePassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const handleSubmit = async (event, { resetForm }) => {
    setIsLoading(true);
    setAlertMessage("");
    setInfoMessage("");
    try {
      await updatePassword({ oldPassword: event.oldPassword, newPassword: event.newPassword });
      setInfoMessage("Password successfully changed.");
      resetForm();
    } catch (e) {
      setAlertMessage(e?.message ?? "Change failed");
    }
    setIsLoading(false);
  };

  return (
    <FormPage title="Change password">
      {alertMessage ? (
        <Alert severity="error" onClose={() => setAlertMessage("")}>{alertMessage}</Alert>
      ) : null}
      {infoMessage ? (
        <Alert severity="success" onClose={() => setInfoMessage("")}>{infoMessage}</Alert>
      ) : null}
      <Formik
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={handleSubmit}
        initialValues={{ oldPassword: "", newPassword: "", confirmPassword: "" }}
      >
        {({ handleSubmit, handleChange, values, errors }) => (
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                name="oldPassword"
                label="Current password"
                type="password"
                value={values.oldPassword}
                onChange={handleChange}
                error={!!errors.oldPassword}
                helperText={errors.oldPassword}
                autoComplete="current-password"
                autoFocus
              />
              <TextField
                name="newPassword"
                label="New password"
                type="password"
                value={values.newPassword}
                onChange={handleChange}
                error={!!errors.newPassword}
                helperText={errors.newPassword ?? "Min 8 chars · 1 upper · 1 lower · 1 number · 1 symbol"}
                autoComplete="new-password"
              />
              <TextField
                name="confirmPassword"
                label="Confirm new password"
                type="password"
                value={values.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                autoComplete="new-password"
              />
              <LoaderButton
                type="submit"
                isLoading={isLoading}
                text="Change password"
                loadingText="Changing…"
                fullWidth
                size="large"
              />
            </Stack>
          </Box>
        )}
      </Formik>
    </FormPage>
  );
}
